"use client";

import { GuideRelatedCard } from "@/app/guides/_components/guide-related-card";
import { HeroCtaButton } from "@/components/ui/hero-cta-button";
import type { ArticleSummary } from "@/data/articles";
import {
  GUIDE_ARTICLE_CARD_RADIUS,
  GUIDE_ARTICLE_LAYOUT_TRANSITION,
  GUIDE_ARTICLE_PAGE_REVEAL,
  guideArticleLayoutId,
} from "@/lib/guide-article-motion";
import { HERO_GRADIENT_TEXT } from "@/lib/light-pages";
import { motion, useReducedMotion } from "framer-motion";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { FaArrowLeft, FaClock } from "react-icons/fa";

type GuideArticlePageViewProps = {
  article: ArticleSummary;
  htmlContent: string;
  relatedArticles: ArticleSummary[];
};

export function GuideArticlePageView({
  article,
  htmlContent,
  relatedArticles,
}: GuideArticlePageViewProps) {
  const router = useRouter();
  const reducedMotion = useReducedMotion();
  const [isExiting, setIsExiting] = useState(false);

  const layoutId = reducedMotion ? undefined : guideArticleLayoutId(article.slug);

  const handleBack = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (reducedMotion) {
      router.push("/guides");
      return;
    }

    setIsExiting(true);
    requestAnimationFrame(() => {
      router.push("/guides");
    });
  };

  return (
    <div className="guides-page-light bg-[#f3f3f3] text-[#1C1C1C]">
      <article className="relative -mt-[calc(5rem+env(safe-area-inset-top))] px-6 pb-16 pt-[calc(5rem+env(safe-area-inset-top)+2rem)] sm:pb-20 sm:pt-[calc(5rem+env(safe-area-inset-top)+2.5rem)]">
        <div className="relative mx-auto max-w-6xl">
          <div className="grid items-start gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(260px,320px)] lg:gap-12">
            <div className="min-w-0">
              <motion.div
                initial={reducedMotion ? false : { opacity: 0, y: 8 }}
                animate={{ opacity: isExiting ? 0 : 1, y: isExiting ? -4 : 0 }}
                transition={{
                  duration: isExiting ? 0.15 : GUIDE_ARTICLE_PAGE_REVEAL.duration,
                  delay: isExiting ? 0 : 0.18,
                }}
              >
                <button
                  type="button"
                  onClick={handleBack}
                  className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-zinc-500 transition hover:text-[#1C1C1C]"
                >
                  <FaArrowLeft className="text-xs" aria-hidden="true" />
                  Retour aux guides
                </button>
              </motion.div>

              <motion.div
                layout={!reducedMotion}
                layoutId={layoutId}
                transition={GUIDE_ARTICLE_LAYOUT_TRANSITION}
                className={`mb-10 max-w-[720px] overflow-hidden ${GUIDE_ARTICLE_CARD_RADIUS} border border-white/10 bg-[#1C1C1C] p-6 shadow-[0_24px_56px_-18px_rgba(0,0,0,0.45)] sm:p-8`}
              >
                <div className="mb-4 flex flex-wrap items-center gap-3">
                  <span className="inline-flex rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-[0.68rem] font-semibold uppercase tracking-wide text-emerald-300">
                    {article.category}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-400">
                    <FaClock className="text-[10px]" aria-hidden="true" />
                    {article.readTime}
                  </span>
                </div>

                <h1 className="mb-4 text-[1.65rem] font-bold leading-[1.12] tracking-[-0.03em] text-white sm:text-[2rem] lg:text-[2.25rem]">
                  {article.title}
                </h1>

                <p className="mb-4 text-sm leading-relaxed text-zinc-400 sm:text-base sm:leading-[1.75]">
                  {article.summary}
                </p>

                <p className="text-xs text-zinc-500 sm:text-sm">
                  Publié le{" "}
                  {new Date(article.publishedAt).toLocaleDateString("fr-FR", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </p>
              </motion.div>

              <motion.div
                className="max-w-[720px]"
                initial={reducedMotion ? false : { opacity: 0, y: 18 }}
                animate={{ opacity: isExiting ? 0 : 1, y: isExiting ? 12 : 0 }}
                transition={{
                  duration: isExiting ? 0.15 : GUIDE_ARTICLE_PAGE_REVEAL.duration,
                  delay: isExiting ? 0 : GUIDE_ARTICLE_PAGE_REVEAL.delay + 0.08,
                }}
              >
                <div
                  className="guide-prose"
                  dangerouslySetInnerHTML={{ __html: htmlContent }}
                />

                <div className="mt-16 overflow-hidden rounded-[1.75rem] border border-zinc-200/80 bg-white/70 px-6 py-10 text-center shadow-[0_20px_60px_-28px_rgba(15,23,42,0.12),inset_0_1px_0_rgba(255,255,255,0.9)] backdrop-blur-sm sm:px-8">
                  <h2 className="mb-3 text-2xl font-bold text-[#1C1C1C] sm:text-[1.65rem]">
                    Prêt à passer à l&apos;action ?
                  </h2>
                  <p className="mx-auto mb-6 max-w-lg text-base text-zinc-600">
                    Discutons de votre projet et trouvons ensemble la meilleure solution.
                  </p>
                  <HeroCtaButton>Prendre un rendez-vous</HeroCtaButton>
                </div>
              </motion.div>
            </div>

            {relatedArticles.length > 0 && (
              <motion.aside
                className="lg:sticky lg:top-28 lg:self-start"
                initial={reducedMotion ? false : { opacity: 0, y: 16 }}
                animate={{ opacity: isExiting ? 0 : 1, y: isExiting ? 8 : 0 }}
                transition={{
                  duration: isExiting ? 0.15 : GUIDE_ARTICLE_PAGE_REVEAL.duration,
                  delay: isExiting ? 0 : GUIDE_ARTICLE_PAGE_REVEAL.delay + 0.16,
                }}
              >
                <div className="rounded-[1.35rem] border border-zinc-200/90 bg-white p-5 shadow-[0_20px_50px_-16px_rgba(15,23,42,0.1)] sm:p-6">
                  <h2 className="mb-1 text-lg font-bold text-[#1C1C1C] sm:text-xl">
                    Recommandé pour vous
                  </h2>
                  <p className="mb-4 text-sm text-zinc-500">
                    D&apos;autres guides liés à{" "}
                    <span className={HERO_GRADIENT_TEXT}>{article.category}</span>
                  </p>
                  <div className="space-y-3">
                    {relatedArticles.map((related) => (
                      <GuideRelatedCard key={related.slug} article={related} />
                    ))}
                  </div>
                </div>
              </motion.aside>
            )}
          </div>
        </div>
      </article>
    </div>
  );
}
